import * as React from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

/**
 * ToastContext + ToastProvider
 * Generic, reusable toast implementation using MUI Snackbar/Alert.
 * Usage:
 *   - Wrap your app in <ToastProvider> (e.g. in App.js or PageLayout).
 *   - Call const { showToast } = useToast(); inside any component.
 *   - showToast({ message: "Saved", severity: "success" });
 */

const ToastContext = React.createContext({
  showToast: () => {}
});

export function ToastProvider({ children }) {
  const [open, setOpen] = React.useState(false);
  const [toast, setToast] = React.useState({
    message: "",
    severity: "info",
    autoHideDuration: 3000
  });

  const showToast = React.useCallback(({ message, severity = "info", autoHideDuration = 3000 }) => {
    setToast({ message, severity, autoHideDuration });
    setOpen(true);
  }, []);

  const handleClose = (_, reason) => {
    if (reason === "clickaway") return;
    setOpen(false);
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={toast.autoHideDuration}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleClose}
          severity={toast.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = React.useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
